import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  GOOGLE_ANALYTICS_ENABLED,
  GOOGLE_ANALYTICS_MEASUREMENT_ID,
  isAdminHost,
} from '../config';
import { useAuth } from '../contexts/AuthContext';
import {
  AnalyticsController,
  AnalyticsEventParameters,
  createAnalyticsController,
} from './analyticsController';
import {
  AnalyticsConsent,
  readAnalyticsConsent,
  saveAnalyticsConsent,
} from './analyticsConsent';
import { describeAnalyticsPage, shouldTrackAnalyticsPage } from './analyticsPages';
import {
  AnalyticsEventName,
  registerAnalyticsEventSink,
} from './analyticsEvents';
import { AnalyticsContext, type AnalyticsContextValue } from './analyticsContext';
import {
  clearGoogleAnalyticsCookies,
  createBrowserGoogleTagRuntime,
  createGoogleAnalyticsDriver,
} from './googleAnalyticsDriver';


const analyticsEnabledForCurrentHost = (): boolean =>
  GOOGLE_ANALYTICS_ENABLED
  && Boolean(GOOGLE_ANALYTICS_MEASUREMENT_ID)
  && typeof window !== 'undefined'
  && !isAdminHost(window.location.hostname);

const createDefaultController = (): AnalyticsController => {
  const driver = createGoogleAnalyticsDriver({
    measurementId: GOOGLE_ANALYTICS_MEASUREMENT_ID,
    enabled: analyticsEnabledForCurrentHost(),
    runtime: createBrowserGoogleTagRuntime(window, document),
    locationOrigin: typeof window !== 'undefined' ? window.location.origin : '',
    clearCookies: () => {
      if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        clearGoogleAnalyticsCookies(document, window.location.hostname);
      }
    },
    onError: (error) => {
      console.warn('Google Analytics could not be started.', error);
    },
  });
  return createAnalyticsController(driver);
};

interface ConsentBannerProps {
  consent: AnalyticsConsent;
  settingsOpen: boolean;
  onChoose: (consent: Exclude<AnalyticsConsent, 'pending'>) => void;
  onClose: () => void;
}

/* Once a visitor has chosen, the dialog reopens only from a Cookie settings
   control (site footer, privacy policy), never from a floating button over the app. */
const ConsentBanner: React.FC<ConsentBannerProps> = ({
  consent,
  settingsOpen,
  onChoose,
  onClose,
}) => {
  if (consent !== 'pending' && !settingsOpen) return null;

  return (
    <section
      role="dialog"
      aria-label="Analytics cookies"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-lg rounded-xl border border-rule-strong bg-paper p-4 text-ink backdrop-blur-xl sm:bottom-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-ink-strong">Analytics cookies</h2>
          <p className="mt-1.5 text-xs leading-5 text-ink">
            With your permission, we use Google Analytics cookies to measure visits and understand
            how IMABIRD is used.
          </p>
          <Link
            to="/privacy"
            className="mt-1.5 inline-block text-[11px] text-muted underline decoration-neutral-600 underline-offset-4 hover:text-ink-strong"
          >
            Read the privacy policy
          </Link>
        </div>
        {consent !== 'pending' ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close cookie settings"
            className="shrink-0 rounded-full p-1 text-muted hover:bg-ink/10 hover:text-ink-strong"
          >
            ×
          </button>
        ) : null}
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => onChoose('denied')}
          className="rounded-lg border border-ink/15 px-3 py-2 text-xs text-ink hover:border-ink/30 hover:bg-ink/5"
        >
          Reject analytics
        </button>
        <button
          type="button"
          onClick={() => onChoose('granted')}
          className="rounded-lg bg-ink px-3 py-2 text-xs font-medium text-ink-inverse hover:bg-paper-sunken"
        >
          Accept analytics
        </button>
      </div>
    </section>
  );
};

interface GoogleAnalyticsProviderProps {
  children: React.ReactNode;
  controller?: AnalyticsController;
  enabled?: boolean;
}

export const GoogleAnalyticsProvider: React.FC<GoogleAnalyticsProviderProps> = ({
  children,
  controller: controllerOverride,
  enabled = analyticsEnabledForCurrentHost(),
}) => {
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();
  const controller = useMemo(
    () => controllerOverride || createDefaultController(),
    [controllerOverride],
  );
  const [consent, setConsent] = useState<AnalyticsConsent>(() => {
    if (!enabled || typeof window === 'undefined') return 'pending';
    return readAnalyticsConsent(window.localStorage);
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The embedded Cut player sits in other apps' frames, where the banner would cover the Cut;
  // without a choice nothing is measured there.
  const framedPlayer = location.pathname.startsWith('/embed/');

  useEffect(() => {
    if (!enabled || consent === 'pending') return;
    void controller.setConsent(consent);
  }, [consent, controller, enabled]);

  useEffect(() => {
    if (!enabled) return;
    controller.identify(user?.uid || null);
  }, [controller, enabled, user?.uid]);

  useEffect(() => {
    if (!enabled) return;
    if (!shouldTrackAnalyticsPage(location.pathname, { isAuthenticated, isLoading })) return;
    const page = describeAnalyticsPage(location.pathname);
    controller.trackPageView(page.path, page.title);
  }, [controller, enabled, isAuthenticated, isLoading, location.pathname]);

  const chooseConsent = useCallback((choice: Exclude<AnalyticsConsent, 'pending'>) => {
    if (typeof window !== 'undefined') {
      saveAnalyticsConsent(window.localStorage, choice);
    }
    setConsent(choice);
    setSettingsOpen(false);
  }, []);

  const trackEvent = useCallback((
    eventName: AnalyticsEventName,
    parameters?: AnalyticsEventParameters,
  ) => {
    controller.trackEvent(eventName, parameters);
  }, [controller]);

  useEffect(() => registerAnalyticsEventSink(trackEvent), [trackEvent]);

  const value = useMemo<AnalyticsContextValue>(() => ({
    trackEvent,
    openCookieSettings: () => setSettingsOpen(true),
    cookieSettingsAvailable: enabled,
  }), [enabled, trackEvent]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
      {enabled && !framedPlayer ? (
        <ConsentBanner
          consent={consent}
          settingsOpen={settingsOpen}
          onChoose={chooseConsent}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within GoogleAnalyticsProvider');
  }
  return context;
};
